import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { validCategoryGroups, isValidHebrewCategoryLabel, type CategoryGroup, type SubCategory } from "@/config/categories";
import { Package } from "lucide-react";

/**
 * Fetches all distinct categories from published products in the DB,
 * then merges with static config. Any DB category not in config
 * gets a dynamic entry with default styling.
 * Categories with 0 products are filtered out.
 */

function slugify(name: string): string {
  return name
    .replace(/\s+/g, "-")
    .replace(/[^\u0590-\u05FFa-zA-Z0-9-]/g, "")
    .toLowerCase();
}

async function fetchDynamicCategories(): Promise<CategoryGroup[]> {
  // Get distinct categories efficiently using a lightweight query
  // Paginate to avoid 1000-row limit but only fetch category column
  const PAGE_SIZE = 1000;
  let allRows: { category: string }[] = [];
  let from = 0;
  while (true) {
    const { data: page, error } = await supabase
      .from("products")
      .select("category")
      .eq("is_published", true).gt("stock", 0)
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    if (!page || page.length === 0) break;
    allRows = allRows.concat(page);
    if (page.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  const HIDDEN_CATEGORIES = new Set(["כללי"]);
  const dbCategories = [...new Set(allRows.map(d => d.category).filter(c => isValidHebrewCategoryLabel(c) && !HIDDEN_CATEGORIES.has(c)))];

  // Build a set of all category names that exist in DB
  const dbCategorySet = new Set(dbCategories);

  // Build a set of all known names (parent + sub)
  const knownNames = new Set<string>();
  for (const g of validCategoryGroups) {
    knownNames.add(g.name);
    g.subcategories.forEach(s => knownNames.add(s.name));
  }

  // Filter static config to only include categories that have products
  // A category has products if its name OR any of its subcategory names exist in DB
  const populatedStaticGroups = validCategoryGroups.filter(g => {
    if (dbCategorySet.has(g.name)) return true;
    return g.subcategories.some(s => dbCategorySet.has(s.name));
  });

  // Find categories in DB that aren't in config
  const unknownCategories = dbCategories.filter(cat => !knownNames.has(cat) && isValidHebrewCategoryLabel(cat));

  if (unknownCategories.length === 0) {
    return populatedStaticGroups;
  }

  // Create dynamic CategoryGroup entries for unknown categories
  const defaultColors = [
    "from-emerald-500 to-teal-400",
    "from-violet-500 to-purple-400",
    "from-rose-500 to-pink-400",
    "from-amber-500 to-yellow-400",
    "from-sky-500 to-blue-400",
    "from-lime-500 to-green-400",
  ];

  const dynamicGroups: CategoryGroup[] = unknownCategories.map((cat, i) => {
    const slug = slugify(cat);
    return {
      name: cat,
      slug: slug || `cat-${i}`,
      icon: Package,
      color: defaultColors[i % defaultColors.length],
      image: "/placeholder.svg",
      circleImage: "/placeholder.svg",
      description: "",
      subcategories: [],
    };
  });

  return [...populatedStaticGroups, ...dynamicGroups];
}

/**
 * Returns all category groups: static config + dynamic from DB.
 * Only includes categories that have at least one published product.
 * Use this instead of importing categoryGroups directly when you need
 * to include categories discovered from products.
 */
export function useDynamicCategories() {
  return useQuery({
    queryKey: ["dynamic-categories"],
    queryFn: fetchDynamicCategories,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Expand selected category group names to include all their subcategory names.
 * Works with both static and dynamic categories.
 */
export function expandCategories(selected: string[], allGroups: CategoryGroup[]): string[] {
  if (selected.length === 0) return [];
  const expanded = new Set<string>(selected);
  for (const cat of selected) {
    const group = allGroups.find(g => g.name === cat);
    if (group) {
      group.subcategories.forEach(sub => expanded.add(sub.name));
    }
  }
  return [...expanded];
}
